/**
 * /performance/admin — HR cycle + template administration (ADR-0013 Phase 4).
 * leaveAdmin-style tabbed admin. Cycles carry the status machine
 * (draft → goal_setting → active → review → closed); templates define the
 * competencies + rating scale + KRA/competency combination rule.
 */
import { createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PerformanceCycleStatus, ReviewTemplateDefinition } from '@kora/types';
import { authedLayoutRoute } from './authed.js';
import {
  createCycle,
  createTemplate,
  listCycles,
  listTemplates,
  transitionCycle,
  type PerfCycle,
} from '../api/performance.js';
import { ApiErrorNotice } from '../components/ApiErrorNotice.js';
import { formatDate } from '../lib/format.js';

export const performanceAdminRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/performance/admin',
  component: PerformanceAdminPage,
});

const NEXT_STATUS: Record<PerformanceCycleStatus, PerformanceCycleStatus | null> = {
  draft: 'goal_setting',
  goal_setting: 'active',
  active: 'review',
  review: 'closed',
  closed: null,
};

type Tab = 'cycles' | 'templates';

function PerformanceAdminPage(): JSX.Element {
  const [tab, setTab] = useState<Tab>('cycles');
  return (
    <section className="p-8 space-y-6 max-w-4xl">
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">Performance administration</h1>
        <p className="text-sm text-gray-400">
          Run review cycles and define the templates managers appraise against.
        </p>
      </header>
      <nav className="flex gap-2 border-b border-gray-900">
        <TabButton active={tab === 'cycles'} onClick={() => setTab('cycles')}>Cycles</TabButton>
        <TabButton active={tab === 'templates'} onClick={() => setTab('templates')}>Templates</TabButton>
      </nav>
      {tab === 'cycles' ? <CyclesPanel /> : <TemplatesPanel />}
    </section>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }): JSX.Element {
  return (
    <button type="button" onClick={onClick} className={`px-3 py-2 text-sm border-b-2 ${active ? 'border-white text-white' : 'border-transparent text-gray-400 hover:text-white'}`}>
      {children}
    </button>
  );
}

// --- Cycles ---------------------------------------------------------------

function CyclesPanel(): JSX.Element {
  const qc = useQueryClient();
  const cycles = useQuery({ queryKey: ['perf', 'cycles'], queryFn: () => listCycles() });
  const templates = useQuery({ queryKey: ['perf', 'templates'], queryFn: () => listTemplates() });
  const [name, setName] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [templateId, setTemplateId] = useState('');

  const refresh = (): Promise<void> => qc.invalidateQueries({ queryKey: ['perf', 'cycles'] });
  const createMut = useMutation({
    mutationFn: () => createCycle({ name, periodStart, periodEnd, ...(templateId ? { templateId } : {}) }),
    onSuccess: () => { setName(''); setPeriodStart(''); setPeriodEnd(''); setTemplateId(''); void refresh(); },
  });

  return (
    <div className="space-y-6">
      <form
        onSubmit={(e) => { e.preventDefault(); createMut.mutate(); }}
        className="rounded border border-gray-900 p-4 space-y-3"
      >
        <p className="text-sm text-white">New cycle</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name"><input value={name} onChange={(e) => setName(e.target.value)} required className={inputCls} placeholder="H1 2026" /></Field>
          <Field label="Template (optional)">
            <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} className={inputCls}>
              <option value="">— none yet —</option>
              {(templates.data?.templates ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Field>
          <Field label="Period start"><input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} required className={inputCls} /></Field>
          <Field label="Period end"><input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} required className={inputCls} /></Field>
        </div>
        <button type="submit" disabled={createMut.isPending} className={primaryBtn}>{createMut.isPending ? 'Creating…' : 'Create cycle'}</button>
        <ApiErrorNotice error={createMut.error} fallback="Could not create cycle." compact />
      </form>

      {cycles.isLoading ? <Placeholder>Loading…</Placeholder> : cycles.isError ? <Placeholder error>Could not load cycles.</Placeholder> : (
        <div className="space-y-3">
          {(cycles.data?.cycles ?? []).length === 0 ? <Placeholder>No cycles yet.</Placeholder> : null}
          {(cycles.data?.cycles ?? []).map((c) => <CycleRow key={c.id} cycle={c} onChanged={() => void refresh()} />)}
        </div>
      )}
    </div>
  );
}

function CycleRow({ cycle, onChanged }: { cycle: PerfCycle; onChanged: () => void }): JSX.Element {
  const next = NEXT_STATUS[cycle.status];
  const mut = useMutation({
    mutationFn: () => transitionCycle(cycle.id, next!),
    onSuccess: () => { onChanged(); },
  });
  return (
    <div className="rounded border border-gray-900 p-4 flex items-center justify-between gap-4">
      <div>
        <p className="text-sm text-white">{cycle.name}</p>
        <p className="text-xs text-gray-400 mt-1">{formatDate(cycle.periodStart)} → {formatDate(cycle.periodEnd)}</p>
      </div>
      <div className="flex items-center gap-3">
        <StatusBadge status={cycle.status} />
        {next ? (
          <button type="button" onClick={() => mut.mutate()} disabled={mut.isPending} className={secondaryBtn}>
            {mut.isPending ? 'Advancing…' : `Advance to ${next}`}
          </button>
        ) : <span className="text-xs text-gray-600">closed</span>}
      </div>
      <ApiErrorNotice error={mut.error} fallback="Transition failed." compact />
    </div>
  );
}

// --- Templates ------------------------------------------------------------

interface CompetencyRow { key: string; label: string }

function TemplatesPanel(): JSX.Element {
  const qc = useQueryClient();
  const templates = useQuery({ queryKey: ['perf', 'templates'], queryFn: () => listTemplates() });
  const [name, setName] = useState('');
  const [competencies, setCompetencies] = useState<CompetencyRow[]>([{ key: 'delivery', label: 'Delivery' }]);
  const [scaleMin, setScaleMin] = useState('1');
  const [scaleMax, setScaleMax] = useState('5');
  const [kraWeight, setKraWeight] = useState('0.7');

  const createMut = useMutation({
    mutationFn: () => {
      const kw = Number(kraWeight);
      const definition: ReviewTemplateDefinition = {
        competencies: competencies.filter((c) => c.key.trim() && c.label.trim()),
        ratingScale: { min: Number(scaleMin), max: Number(scaleMax) },
        combinationRule: { kraWeight: kw, competencyWeight: Number((1 - kw).toFixed(4)) },
      };
      return createTemplate({ name, definition });
    },
    onSuccess: () => { setName(''); void qc.invalidateQueries({ queryKey: ['perf', 'templates'] }); },
  });

  const setComp = (i: number, patch: Partial<CompetencyRow>): void =>
    setCompetencies((cs) => cs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));

  return (
    <div className="space-y-6">
      <form onSubmit={(e) => { e.preventDefault(); createMut.mutate(); }} className="rounded border border-gray-900 p-4 space-y-3">
        <p className="text-sm text-white">New template</p>
        <Field label="Name"><input value={name} onChange={(e) => setName(e.target.value)} required className={inputCls} placeholder="Standard appraisal" /></Field>

        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-gray-500">Competencies</p>
          {competencies.map((c, i) => (
            <div key={i} className="flex gap-2">
              <input value={c.key} onChange={(e) => setComp(i, { key: e.target.value })} placeholder="key" className={`${inputCls} w-1/3`} />
              <input value={c.label} onChange={(e) => setComp(i, { label: e.target.value })} placeholder="Label" className={`${inputCls} flex-1`} />
              <button type="button" onClick={() => setCompetencies((cs) => cs.filter((_, idx) => idx !== i))} className={secondaryBtn} aria-label="Remove competency">×</button>
            </div>
          ))}
          <button type="button" onClick={() => setCompetencies((cs) => [...cs, { key: '', label: '' }])} className={secondaryBtn}>Add competency</button>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Scale min"><input type="number" value={scaleMin} onChange={(e) => setScaleMin(e.target.value)} className={inputCls} /></Field>
          <Field label="Scale max"><input type="number" value={scaleMax} onChange={(e) => setScaleMax(e.target.value)} className={inputCls} /></Field>
          <Field label="KRA weight (0–1)"><input type="number" step="0.05" min="0" max="1" value={kraWeight} onChange={(e) => setKraWeight(e.target.value)} className={inputCls} /></Field>
        </div>
        <p className="text-xs text-gray-500">Competency weight is the remainder: {Number((1 - Number(kraWeight)).toFixed(4))}.</p>
        <button type="submit" disabled={createMut.isPending} className={primaryBtn}>{createMut.isPending ? 'Creating…' : 'Create template'}</button>
        <ApiErrorNotice error={createMut.error} fallback="Could not create template." compact />
      </form>

      {templates.isLoading ? <Placeholder>Loading…</Placeholder> : (
        <div className="space-y-3">
          {(templates.data?.templates ?? []).length === 0 ? <Placeholder>No templates yet.</Placeholder> : null}
          {(templates.data?.templates ?? []).map((t) => (
            <div key={t.id} className="rounded border border-gray-900 p-4">
              <p className="text-sm text-white">{t.name}</p>
              <p className="text-xs text-gray-400 mt-1">
                {t.definition.competencies.length} competencies · scale {t.definition.ratingScale.min}–{t.definition.ratingScale.max} · KRA {t.definition.combinationRule.kraWeight} / competency {t.definition.combinationRule.competencyWeight}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- shared bits ----------------------------------------------------------

export function StatusBadge({ status }: { status: string }): JSX.Element {
  return <span className="text-[0.65rem] uppercase tracking-wide rounded border border-gray-700 px-2 py-0.5 text-gray-300">{status.replace(/_/g, ' ')}</span>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block space-y-1">
      <span className="text-xs text-gray-400">{label}</span>
      {children}
    </label>
  );
}

function Placeholder({ children, error }: { children: React.ReactNode; error?: boolean }): JSX.Element {
  return <div className={`rounded border p-6 text-center text-sm ${error ? 'border-red-900 bg-red-950/40 text-red-300' : 'border-gray-900 text-gray-500'}`}>{children}</div>;
}

const inputCls = 'w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm';
const primaryBtn = 'rounded bg-green-700 text-white text-xs px-3 py-1.5 disabled:opacity-50';
const secondaryBtn = 'rounded border border-gray-700 text-xs px-3 py-1.5 hover:bg-gray-900';
